import * as React from 'react';
import { compose, setDisplayName, withHandlers, withProps, withState } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { ReportSettings, ReportSettingsChangeHandler } from 'logic/reports/ReportSettings';
import { Input } from 'ui/shared/Input';
import { DistributionOption } from 'ui/pages/designer/sections/distribution/DistributionOption';
import { hasFeature } from 'logic/reports/reportType';
import { ReportType } from 'types';
import { Required } from 'ui/pages/designer/Required';

import './EmailDistribution.css';

export type OwnProps = {
    reportType: ReportType;
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

const labelStyle = {
    marginTop: '1em',
};

const RawEmailDistribution = ({
    reportType,
    reportSettings,
    onChange,
    emails,
    handleChangeEmails,
    handleBlurEmails,
    fileTypes,
    handleChangeFileType,
    t,
}: OwnProps & RecomposeProps & TranslateProps) =>
    <div id="edit-report-email-distribution">
        <div style={ labelStyle }>
            <label htmlFor="edit-report-email-addresses">
                { t('WC_REPORT_EMAIL_ADDRESSES') }
            </label>
        </div>
        <div className="inline-child-divs">
            <Input
                id="edit-report-email-addresses"
                value={ emails }
                onChange={ handleChangeEmails }
                onBlur={ handleBlurEmails }
                placeholder={ t('WC_REPORT_EMAIL_ADDRESSES_PLACEHOLDER') }
                /* TODO... make it auto-expand vertically in JS */
            />
            <Required visible={ !emails }/>
        </div>
        <div style={ labelStyle } className="inline-child-divs">
            <label>
                { t('WC_REPORT_FILE_FORMATS') }
            </label>
            <Required visible={ Object.keys(fileTypes).reduce((v, ft) => v && !fileTypes[ft], true) }/>
        </div>
        <DistributionOption method="email" type="PDF" textKey="6.1241.0.36" fileTypes={ fileTypes } onChange={ handleChangeFileType }/>
        <DistributionOption method="email" type="CSV" textKey="6.1241.0.35" fileTypes={ fileTypes } onChange={ handleChangeFileType }/>
        <DistributionOption method="email" type="XLS" textKey="6.1241.0.34" fileTypes={ fileTypes } onChange={ handleChangeFileType }/>
        <DistributionOption method="email" type="XML" textKey="Extensible_Markup_Language" fileTypes={ fileTypes } onChange={ handleChangeFileType }/>
        { hasFeature(reportType, 'hasHeatMap') &&
            <DistributionOption method="email" type="Image" textKey="1613" fileTypes={ fileTypes } onChange={ handleChangeFileType }/>
        }
        { hasFeature(reportType, 'hasSummary') &&
            <DistributionOption method="email" type="Summary" textKey="IncludeSummaryInformation" fileTypes={ fileTypes } onChange={ handleChangeFileType }/>
        }
    </div>
;

type RecomposeProps = {
    emails: string;
    handleBlurEmails: React.FocusEventHandler<HTMLInputElement>;
    handleChangeEmails: React.ChangeEventHandler<HTMLInputElement>;
    fileTypes: { [type: string]: boolean };
    handleChangeFileType: React.ChangeEventHandler<HTMLInputElement>;
};
type _RecomposeProps = RecomposeProps & {
    _setEmails: (e: string) => void;
};

const hasSameElements = (array1: string[], array2: string[]): boolean => {
    if (array1.length !== array2.length) {
        return false;
    }

    for (let i = 0; i < array1.length; i++) {
        if (array2[i] !== array1[i]) {
            return false;
        }
    }

    return true;
};


export const EmailDistribution = compose<OwnProps & RecomposeProps & TranslateProps, OwnProps>(
    setDisplayName('EmailDistribution'),
    translate(),
    withProps(({ reportSettings }: OwnProps) => ({
        fileTypes: reportSettings.emailFileTypes.reduce((obj, fileType) => ({ ...obj, [fileType]: true }), {}),
    })),
    withState('emails', '_setEmails', ({ reportSettings }: OwnProps) =>
        reportSettings.emails.join()
    ),
    withHandlers<OwnProps & _RecomposeProps, Partial<RecomposeProps>>({
        handleChangeEmails: ({ _setEmails }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            _setEmails(e.currentTarget.value);
        },
        handleBlurEmails: ({ reportSettings, onChange, emails }: OwnProps & RecomposeProps) => (e: React.FocusEvent<HTMLInputElement>) => {
            const currentEmails = reportSettings.emails;
            const tentativeEmails = emails.split(/[\s;,\r\n]/gm).filter(a => !!a);
            if (hasSameElements(currentEmails, tentativeEmails)) {
                // Don't update the settings if the email addresses haven't changed
                return;
            }
            onChange(reportSettings => reportSettings.setEmails(tentativeEmails));
        },
        handleChangeFileType: ({ onChange }) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const { type } = e.currentTarget.dataset;
            onChange(reportSettings =>
                e.currentTarget.checked
                    ? reportSettings.addEmailFileType(type!)
                    : reportSettings.removeEmailFileType(type!)
            );
        },
    }),

)(RawEmailDistribution);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/distribution/EmailDistribution.tsx